import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Filter, tab and page state that lives in the URL query string instead of
 * component state.
 *
 * The point is the Back button. An admin narrows the users table to declined
 * KYC, opens one, presses Back — and with `useState` lands on the unfiltered
 * table, because the list unmounted on the way out. Held in the URL, the
 * filters are part of the history entry the browser returns to, and the same
 * URL pastes into a Slack thread as a link to exactly that view.
 *
 * `defaults` names every key the screen owns and the value each takes when
 * the URL does not mention it. Define it at module scope: the read below is
 * memoised on it, and an object literal in render would rebuild the state
 * every time. A key whose default is a number is parsed back from the URL as
 * one; anything unparseable falls back to the default rather than sending
 * NaN as a page offset.
 *
 * Writes replace the current history entry rather than push a new one, so
 * ten keystrokes in a search box are one entry, not ten Backs. Keys set back
 * to their default are removed, which keeps `/users` reading as `/users`
 * until an admin has actually changed something.
 */
type UrlValue = string | number;

export function useUrlState<T extends Record<string, UrlValue>>(
  defaults: T,
): readonly [T, (patch: Partial<T>) => void] {
  const [searchParams, setSearchParams] = useSearchParams();

  const state = useMemo(() => readState(searchParams, defaults), [searchParams, defaults]);

  // react-router's functional `setSearchParams` starts from the params of the
  // last render, not the last write, so two writes in one event would each
  // start from the same stale base and the second would undo the first. This
  // ref is advanced on every write so each one starts from the one before it.
  const latest = useRef(searchParams);
  useEffect(() => {
    latest.current = searchParams;
  }, [searchParams]);

  const update = useCallback(
    (patch: Partial<T>) => {
      const next = new URLSearchParams(latest.current);
      for (const [key, value] of Object.entries(patch)) {
        if (value === undefined || value === defaults[key]) next.delete(key);
        else next.set(key, String(value));
      }
      latest.current = next;
      setSearchParams(next, { replace: true });
    },
    [defaults, setSearchParams],
  );

  return [state, update];
}

function readState<T extends Record<string, UrlValue>>(params: URLSearchParams, defaults: T): T {
  const state: Record<string, UrlValue> = {};
  for (const [key, fallback] of Object.entries(defaults)) {
    const raw = params.get(key);
    if (raw === null) {
      state[key] = fallback;
    } else if (typeof fallback === 'number') {
      const parsed = Number(raw);
      state[key] = Number.isFinite(parsed) ? parsed : fallback;
    } else {
      state[key] = raw;
    }
  }
  // Built key-for-key from `defaults`, so it has exactly T's shape.
  return state as T;
}

/**
 * The selected tab on a tabbed screen, held in the URL as `?tab=`.
 *
 * Separate from `useUrlState` because switching tab drops every other key:
 * the filters in the query string belong to the tab that wrote them, and an
 * `offset=50` left behind by In-App would otherwise open Push on page 3. That
 * is also what happened before, when the old tab unmounted and took its state
 * with it. A `tab` not in `tabs` — a mistyped link — falls back to the default
 * rather than rendering no tab at all.
 */
export function useUrlTab(
  tabs: readonly { readonly value: string }[],
  defaultTab: string,
): readonly [string, (tab: string) => void] {
  const [searchParams, setSearchParams] = useSearchParams();

  const requested = searchParams.get('tab');
  const tab =
    requested !== null && tabs.some((item) => item.value === requested) ? requested : defaultTab;

  const setTab = useCallback(
    (next: string) => {
      setSearchParams(next === defaultTab ? {} : { tab: next }, { replace: true });
    },
    [defaultTab, setSearchParams],
  );

  return [tab, setTab];
}
